/*
  Tecnolatina — Motor de seleção de exaustor da calculadora de kit.
  Módulo PURO (sem DOM, sem side effects). Testável em Node (module.exports) e
  disponível no browser como window.AlventoKitEngine.

  ── MODELO FÍSICO (aproximação comercial, honesta) ─────────────────────────────
  • Curva do ventilador LINEAR:  Q(p) = Qmax × (1 − p/Pmax)
      ⚠️ APROXIMAÇÃO. Quando houver a curva Q×p real do fabricante, trocar
      qEntregue() por interpolação da curva (ou ajustar Qmax/Pmax ponto a ponto).
  • Perda do sistema por VELOCIDADE do ar em duto flexível corrugado:
      v = Q/A ;  perda_por_metro = fatorAtritoFlex × v²  (Pa/m)
      Calibrado p/ ~4 Pa/m em duto 100 mm a ~130 m³/h (faixa 3–6 Pa/m típica).
  • Perda de terminação (grelha/chapéu) somada como constante (config).
  • Ponto de operação (Q depende de p e p depende de Q): resolvido ANALITICAMENTE
      (k·Q² + Q − c = 0 → raiz positiva) — sem oscilação de iteração.

  Todas as constantes ajustáveis vivem em CONFIG.
*/
(function (root) {
  'use strict';

  var CONFIG = {
    curvaEquivM: 1.2,        // cada curva de 90° ≈ 1,2 m de duto reto
    margemSeguranca: 0.15,   // exige Q_entregue ≥ vazao_necessaria × 1,15
    fatorAtritoFlex: 0.19,   // Pa/m por (m/s)² — calibrado p/ duto flexível residencial
    axialPMax: 50,           // Pa — pressão máx assumida dos axiais (calibrado ao mercado:
                             // axial 100 ~5 m², axial 150 ~7–8 m²; trocar por curva real do fornecedor)
    perdaTerminacao: { veneziana: 10, chapeu: 15, fixa: 5 }, // Pa (configurável)
    terminacaoPorCenario: {  // qual terminação cada cenário usa (afeta só a física)
      parede_externa: 'veneziana',
      forro_fachada: 'veneziana',
      forro_telhado: 'chapeu',
      shaft: 'fixa',
    },
    // O inline SÓ pode ser instalado onde há forro/espaço na linha do duto.
    // Sem forro (parede direta, shaft) → apenas axial; se o axial não atende → encaminhar.
    forroPorCenario: {
      parede_externa: false,
      forro_fachada: true,
      forro_telhado: true,
      shaft: false,
    },
  };

  // Dados REAIS dos produtos (kickoff). A ORDEM = prioridade de seleção:
  // axial (mais barato) → inline por família crescente; dentro da família L (silêncio) antes de H.
  var MODELOS = [
    { id: 'axial-100', tipo: 'axial', familia: 'Axial 100', diam: 100, qMax: 160 /* pMax = axialPMax */ },
    { id: 'axial-150', tipo: 'axial', familia: 'Axial 150', diam: 150, qMax: 240 },
    { id: 'tc100-L', tipo: 'inline', familia: 'TC-100', vel: 'L', diam: 100, qMax: 220, pMax: 230, db: 25, w: 60 },
    { id: 'tc100-H', tipo: 'inline', familia: 'TC-100', vel: 'H', diam: 100, qMax: 280, pMax: 180, db: 30, w: 60 },
    { id: 'tc150-L', tipo: 'inline', familia: 'TC-150', vel: 'L', diam: 150, qMax: 475, pMax: 250, db: 30, w: 70 },
    { id: 'tc150-H', tipo: 'inline', familia: 'TC-150', vel: 'H', diam: 150, qMax: 560, pMax: 300, db: 35, w: 70 },
    { id: 'tc200-L', tipo: 'inline', familia: 'TC-200', vel: 'L', diam: 200, qMax: 830, pMax: 280, db: 45, w: 110 },
    { id: 'tc200-H', tipo: 'inline', familia: 'TC-200', vel: 'H', diam: 200, qMax: 980, pMax: 350, db: 50, w: 110 },
  ];

  function areaM2(diam_mm) { var r = diam_mm / 2000; return Math.PI * r * r; }

  function perdaPorMetro(diam_mm, q_m3h, cfg) {
    var A = areaM2(diam_mm);
    var v = (q_m3h / 3600) / A; // m/s
    return cfg.fatorAtritoFlex * v * v; // Pa/m
  }

  // Ponto de operação analítico: Q = Qmax(1 − (a·Q² + T)/Pmax)
  //   ⇒ (Qmax·a/Pmax)·Q² + Q − Qmax(1 − T/Pmax) = 0  (raiz positiva)
  function qEntregue(modelo, compEquiv, perdaTerm, cfg) {
    var pMax = (modelo.pMax != null) ? modelo.pMax : cfg.axialPMax;
    if (perdaTerm >= pMax) return 0; // terminação sozinha já zera o ventilador
    var A = areaM2(modelo.diam);
    var a = cfg.fatorAtritoFlex * compEquiv / (3600 * 3600 * A * A); // Pa por (m³/h)²
    var k = modelo.qMax * a / pMax;
    var c = modelo.qMax * (1 - perdaTerm / pMax);
    if (k <= 0) return Math.round(Math.max(0, c)); // sem duto: Q = c
    var q = (-1 + Math.sqrt(1 + 4 * k * c)) / (2 * k);
    return Math.round(Math.max(0, q));
  }

  function compEquivalente(distancia, curvas, cfg) {
    var n = (curvas >= 3) ? 3 : curvas;
    return distancia + n * cfg.curvaEquivM;
  }

  /*
    Seleciona o exaustor para o caminho do cliente.
    @param vazaoNec  m³/h necessários (vindo da etapa 1)
    @param cenario   'parede_externa' | 'forro_fachada' | 'forro_telhado' | 'shaft'
    @param distancia m (0,5–6)
    @param curvas    0..3 (3 = "3+")
    @param cfgIn     override opcional de CONFIG
    @returns { compEquiv, perdaTerm, termTipo, alvo, vazaoNec, avaliados[], escolhido|null }
       avaliados = todos os modelos com { modelo, qEntregue, atende }
       escolhido = 1º da ordem de preferência que atende (ou null → encaminhar atendimento)
  */
  function selecionar(vazaoNec, cenario, distancia, curvas, cfgIn) {
    var cfg = cfgIn || CONFIG;
    var compEquiv = compEquivalente(distancia, curvas, cfg);
    var termTipo = cfg.terminacaoPorCenario[cenario] || 'veneziana';
    var perdaTerm = cfg.perdaTerminacao[termTipo];
    var alvo = vazaoNec * (1 + cfg.margemSeguranca);
    var temForro = !!cfg.forroPorCenario[cenario];
    var avaliados = MODELOS.map(function (m) {
      var q = qEntregue(m, compEquiv, perdaTerm, cfg);
      var elegivel = (m.tipo === 'axial') || temForro; // inline exige forro
      return { modelo: m, qEntregue: q, atende: q >= alvo, elegivel: elegivel };
    });
    var escolhido = null;
    for (var i = 0; i < avaliados.length; i++) {
      if (avaliados[i].elegivel && avaliados[i].atende) { escolhido = avaliados[i]; break; }
    }
    // Motivo do encaminhamento: se um inline resolveria mas não há forro, é "sem_forro"
    var motivo = null;
    if (!escolhido) {
      var inlineResolveria = avaliados.some(function (a) { return a.modelo.tipo === 'inline' && a.atende; });
      motivo = (!temForro && inlineResolveria) ? 'sem_forro' : 'excede_capacidade';
    }
    return {
      compEquiv: compEquiv, perdaTerm: perdaTerm, termTipo: termTipo, temForro: temForro,
      alvo: Math.round(alvo), vazaoNec: vazaoNec, avaliados: avaliados, escolhido: escolhido, motivo: motivo,
    };
  }

  // Q entregue pelo axial do mesmo diâmetro do escolhido — para o card de honestidade.
  function axialDeReferencia(resultado, diam) {
    var alvoTipo = diam || (resultado.escolhido ? resultado.escolhido.modelo.diam : 100);
    for (var i = 0; i < resultado.avaliados.length; i++) {
      var a = resultado.avaliados[i];
      if (a.modelo.tipo === 'axial' && a.modelo.diam === alvoTipo) return a;
    }
    return resultado.avaliados[0]; // fallback: axial-100
  }

  var API = {
    CONFIG: CONFIG, MODELOS: MODELOS,
    areaM2: areaM2, perdaPorMetro: perdaPorMetro, qEntregue: qEntregue,
    compEquivalente: compEquivalente, selecionar: selecionar, axialDeReferencia: axialDeReferencia,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  root.AlventoKitEngine = API;
})(typeof window !== 'undefined' ? window : this);
